/*
 * Funnel indicators: the pure maths (no DOM, no d3, no network), so it can be tested on its own. The design of record, and every number below, is in docs/FUNNEL_STEP_INDICATORS.md.
 *
 * A funnel is an ordered list of pages the user has tagged as steps, ending at the goal. Between consecutive steps a thick ribbon runs behind the page boxes, from one box to the next, getting
 * narrower as fewer visitors are left. Its colour (when colouring by loss) says how much is lost BEFORE THE NEXT STEP: blue = doing fine, yellow = lost a little, red = lost a lot.
 * The numbers appear on hover only.
 */
import { FunnelMode } from "./funnelProgress";

export type FunnelLevel = "ok" | "little" | "lot";

/** Placeholder bands (docs/FUNNEL_STEP_INDICATORS.md): to be tuned against real data. */
export const LOSS_LITTLE_FROM = 0.4;
export const LOSS_LOT_FROM = 0.65;

/** blue / yellow / red. The red is the one from Chris's own sketch over a screenshot of the dashboard. */
export const FUNNEL_COLOURS: Record<FunnelLevel, string> = { ok: "#3a8ddc", little: "#f2b632", lot: "#e5403f" };
/** The step pills and the picking outline: a quiet neutral gray. Blue competes with the colours that mean bubble categories (Chris, 2026-10-17). */
export const FUNNEL_BADGE = "#6f7782";
/** The ribbon colour when it is not colouring by loss: the dashboard's own flow-line blue. */
export const FUNNEL_NEUTRAL = "#5078c8";

/** What happened to the visitors who reached one funnel step, counted over the date range being viewed. */
export interface StepMetric {
  page: string;
  /** the page that comes next in the funnel (the goal, for the last step) */
  next: string;
  /** visitors (sessions) that reached this page */
  visits: number;
  /** went STRAIGHT on to the next step */
  wentOn: number;
  /** went somewhere else on the site */
  elsewhere: number;
  /** left the site from this page (in Loose: dropped off, i.e. the visit ended without reaching the next step) */
  left: number;
  /** still on the site and neither gone on nor dropped off yet (absent in older answers = none) */
  inPlay?: number;
  /** Strict or Loose: what "went on" and "dropped off" mean (absent = Strict) */
  mode?: FunnelMode;
  /** a replay only: the instant of the day these numbers describe (epoch ms), so they can say so */
  asOf?: number;
}

export const metricKey = (page: string, next: string) => `${page}>${next}`;

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * The drop off at a step: the share of its visitors who have dropped off, that is who did NOT go on and are no longer in play. A visitor still in play (still on the site, could yet reach the
 * next step) is not a drop off. What "go on" means, straight or in order, is decided where the numbers are counted (funnelProgress.ts).
 */
export const lossOf = (m: StepMetric): number => (m.visits > 0 ? clamp01((m.elsewhere + m.left) / m.visits) : 0);

export const levelOf = (loss: number): FunnelLevel => (loss >= LOSS_LOT_FROM ? "lot" : loss >= LOSS_LITTLE_FROM ? "little" : "ok");

/** Mix a #rrggbb colour toward black. */
export const darken = (hex: string, amount: number): string => {
  const n = parseInt(hex.replace("#", ""), 16);
  const k = 1 - clamp01(amount);
  const part = (shift: number) => Math.round(((n >> shift) & 255) * k).toString(16).padStart(2, "0");

  return `#${part(16)}${part(8)}${part(0)}`;
};

/** The ribbon's colour: by how much is lost, or the one neutral blue. */
export const ribbonColour = (level: FunnelLevel, byLoss: boolean): string => (byLoss ? FUNNEL_COLOURS[level] : FUNNEL_NEUTRAL);

/** The tooltip card is the ribbon's own colour, darkened so white text reads on it (plain yellow does not). */
export const tooltipBackgroundFor = (hex: string): string => darken(hex, 0.26);
export const tooltipBackground = (level: FunnelLevel): string => tooltipBackgroundFor(FUNNEL_COLOURS[level]);

export const percent = (x: number): string => `${Math.round(clamp01(x) * 100)}%`;

/** A share for the wrap-up: whole percent from 10% up, one decimal below it ("2%", "1.1%", "0.4%"), and "<0.1%" for something real but tiny, so a small success rate is never shown as a flat 0. */
export const pctText = (x: number, hadAny = true): string => {
  const v = clamp01(x) * 100;
  if (v === 0) return hadAny ? "<0.1%" : "0%";
  if (v < 0.05) return "<0.1%";
  if (v < 10) return `${Number(v.toFixed(1))}%`;

  return `${Math.round(v)}%`;
};

/** One step of the wrap-up. */
export interface SummaryStep {
  page: string;
  next: string;
  /** visitors who reached this step */
  reached: number;
  /** of those, how many have dropped off (went elsewhere, or their visit ended) */
  dropped: number;
  /** dropped / reached: the same "NN% drop off" as the ribbon's tooltip */
  dropPct: number;
  /** still on the site, could yet go on: not a drop off, not a success */
  inPlay: number;
}

/** The whole funnel in one answer: how many came in, what each step lost, how many made it to the goal. */
export interface FunnelSummary {
  entered: number;
  steps: SummaryStep[];
  reachedGoal: number;
  /** reachedGoal / entered: the funnel's overall success rate */
  successPct: number;
  /** across all steps: still on the site and undecided */
  inPlay: number;
  /** across all steps: dropped off */
  dropped: number;
  mode?: FunnelMode;
  asOf?: number;
}

/**
 * The funnel's wrap-up from its per-step numbers (the same numbers the ribbons and their tooltips show, in order, one per step before the goal). Chris, 2026-10-18: "10000 visitors came in,
 * 45% drop first step, 32% drop second step... with only 109 visitors making it to the last step or goal page. That is a [..] percentage success (2% successful funnel)."
 *  - ENTERED is the first step's visits: everyone who reached step 1 (the funnel only follows people from there);
 *  - a step's drop is its dropped / its reached, the share of the people who GOT to it (not of everyone who entered);
 *  - REACHED THE GOAL is the last step's "went on";
 *  - the success rate is reachedGoal / entered. Visitors still on the site count in neither the drops nor the successes, so they are reported on their own ("still in play"):
 *    they are why the rate of a funnel that is still filling can rise.
 * Returns null when there is nothing to summarise: no steps, or a step whose numbers have not arrived yet.
 */
export const summariseFunnel = (metrics: Array<StepMetric | undefined>): FunnelSummary | null => {
  if (!metrics.length || metrics.some((m) => !m)) return null;
  const ms = metrics as StepMetric[];
  const steps: SummaryStep[] = ms.map((m) => ({ page: m.page, next: m.next, reached: m.visits, dropped: m.elsewhere + m.left, dropPct: lossOf(m), inPlay: m.inPlay || 0 }));
  const entered = ms[0].visits;
  const reachedGoal = ms[ms.length - 1].wentOn;

  return {
    entered,
    steps,
    reachedGoal,
    successPct: entered > 0 ? clamp01(reachedGoal / entered) : 0,
    inPlay: steps.reduce((t, x) => t + x.inPlay, 0),
    dropped: steps.reduce((t, x) => t + x.dropped, 0),
    mode: ms[0].mode,
    asOf: ms[0].asOf,
  };
};

const escapeHtml = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c] as string));

/**
 * The tooltip's HTML, in the anatomy of the app's own tooltips (tooltipService.ts's buildTooltipHtml and the `tooltip-v2` rules in Styles.scss): a two-column header, then rows of
 * label and bold value with a faint sub-value. The one addition is a small stacked bar, so the split reads before any number does.
 */
/** " · as of 2:30 PM" for a replay's numbers (they describe an instant, not the whole day), or nothing. */
export const asOfLabel = (asOf: number | undefined): string =>
  typeof asOf === "number" && Number.isFinite(asOf) ? ` · as of ${new Date(asOf).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}` : "";

export const funnelTooltipHtml = (m: StepMetric, stepNumber: number, totalSteps: number): string => {
  const v = Math.max(1, m.visits);
  const next = escapeHtml(m.next);
  const inPlay = m.inPlay || 0;
  // The rows say what the visitors DID, in the words of the mode. Loose has no "elsewhere": a visitor who wandered off is still in play until their visit ends.
  const rows: Array<{ label: string; count: number; opacity: number }> =
    m.mode === "loose"
      ? [
          { label: `Reached ${next}`, count: m.wentOn, opacity: 1 },
          { label: "Still in play", count: inPlay, opacity: 0.6 },
          { label: "Dropped off", count: m.left, opacity: 0.22 },
        ]
      : [
          { label: `Went straight on to ${next}`, count: m.wentOn, opacity: 1 },
          { label: "Went elsewhere on the site", count: m.elsewhere, opacity: 0.5 },
          { label: "Left the site", count: m.left, opacity: 0.22 },
          ...(inPlay > 0 ? [{ label: "Still on this step", count: inPlay, opacity: 0.75 }] : []),
        ];
  const row = (r: { label: string; count: number; opacity: number }) =>
    `<div class="tooltip-row"><span class="tooltip-row-label"><i class="tooltip-funnel-key" style="opacity:${r.opacity}"></i>${r.label}</span><span class="tooltip-row-value">${r.count.toLocaleString()}<span class="tooltip-row-sub">${percent(r.count / v)}</span></span></div>`;

  return `
    <div class="tooltip-header">
      <div class="tooltip-header-col"><span class="tooltip-header-label">Step ${stepNumber} of ${totalSteps}${asOfLabel(m.asOf)}</span><span class="tooltip-header-value">${percent(lossOf(m))}<span class="tooltip-header-unit">drop off</span></span></div>
      <div class="tooltip-header-col"><span class="tooltip-header-label">Next</span><span class="tooltip-header-value tooltip-header-next">${escapeHtml(m.next)}</span></div>
    </div>
    <div class="tooltip-funnel-bar">${rows.map((r) => `<span style="flex:${r.count / v};opacity:${r.opacity}"></span>`).join("")}</div>
    <div class="tooltip-body">
      ${rows.map(row).join("")}
    </div>`;
};
